import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Form, Formik } from 'formik';
import { memo, useCallback, useState } from 'react';
import { useMountedState } from 'react-use';

import { ChevronLeftIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { PaymentFields } from '~/components/Workspace/Checkout/PaymentFields/PaymentFields';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdatePaymentMethodMutation } from '~/swr-hooks/subscriptions/useUpdatePaymentMethodMutation';

const SUCCESS_MESSAGE_TIMEOUT = 2000;

export type PaymentUpdateFormProps = {
  onBack: () => void;
};

export const PaymentUpdateForm = memo(({ onBack }: PaymentUpdateFormProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const [showSuccess, setShowSuccess] = useState(false);
  const isMounted = useMountedState();
  const updatePaymentMethodMutation = useUpdatePaymentMethodMutation();
  const stripe = useStripe();
  const elements = useElements();

  // Returns the mutation's promise so Formik's `isSubmitting` resolves on both outcomes;
  // `mutation.error` (rendered below) already surfaces the failure, so the rejection is
  // swallowed here rather than left for Formik to rethrow.
  const updatePayment = useCallback(async () => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      return;
    }

    try {
      await updatePaymentMethodMutation.mutateAsync({ stripe, elements, workspaceId });
    } catch {
      return;
    }

    if (!isMounted()) {
      return;
    }

    // Show success state
    setShowSuccess(true);

    // Show a screen confirming the payment was updated then
    // return to the original screen, using setTimeout to achieve that UX
    setTimeout(() => {
      if (isMounted()) {
        onBack();
      }
    }, SUCCESS_MESSAGE_TIMEOUT);
  }, [currentWorkspace?.id, elements, isMounted, onBack, stripe, updatePaymentMethodMutation]);

  const handleSubmit = useCallback(() => updatePayment(), [updatePayment]);

  const isStripeUnready = !stripe || !elements;

  if (isStripeUnready) {
    return (
      <div className="py-12 text-center">
        <div className="ads:text-secondary text-14 text-gray-11">Loading payment system...</div>
      </div>
    );
  }

  // Show success message if payment was updated
  if (showSuccess) {
    return (
      <div className="py-8 text-center">
        <div className="mb-4 text-48">✓</div>
        <h2 className="mb-2 font-semibold ads:text-primary text-20 text-gray-12">Payment Updated Successfully</h2>
        <p className="ads:text-secondary text-14 text-gray-11">Your payment information has been updated.</p>
      </div>
    );
  }

  return (
    <Formik initialValues={{}} onSubmit={handleSubmit}>
      {({ isSubmitting }) => {
        return (
          <Form>
            <Button
              onClick={onBack}
              appearance="ghost"
              color="gray"
              size="small"
              className="mb-4 flex items-center"
              disabled={isSubmitting}
            >
              <ChevronLeftIcon className="mr-1" size="sm" />
              Back
            </Button>

            <h2 className="mb-4 font-semibold ads:text-primary text-20 text-gray-12">Update payment method</h2>

            <PaymentFields disabled={isSubmitting} />

            {updatePaymentMethodMutation.error && (
              <div className="mt-4 ads:text-error text-14 text-red-9">{updatePaymentMethodMutation.error.message}</div>
            )}

            <Button
              appearance="filled"
              className="mt-4"
              color="blue"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              size="large"
            >
              Confirm
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
});

PaymentUpdateForm.displayName = 'PaymentUpdateForm';
